import _ from 'lodash';
import Vue from 'vue';

import sourcesFields from '../../constants/sourcesFields';
import MergeInvestigatorApi from '../../api/MergeInvestigatorApi';
import CredentialsApi from '../../api/CredentialsApi';

const state = {
  papasMergeQuestions: [],
  sonsMergeQuestions: [],
  loading: false,
  papaInputTypes: {
    approved: 'approved',
    loan: 'loan',
    clearing: 'clearing',
    addCreds: 'addCreds',
    bug: 'bug',
  },
  cardInputTypes: {
    addCreds: 'addCreds',
    excluded: 'excluded',
    bug: 'bug',
  },
};

const getters = {
  allPapasAnswered: state => _.every(state.papasMergeQuestions, q => q.input && (!isAddCredsInput(q) || hasSource(q))),
  allSonsAnswered: state => _.every(state.sonsMergeQuestions, q => q.input && (!isAddCredsInput(q) || q.bankSource)),
  allQuestionsAnswered: (state, getters) => {
    // we need this line otherwise vuex doesn't watch the second getter because the first one is always false
    const sonsAnswered = getters.allSonsAnswered;
    return getters.allPapasAnswered && sonsAnswered;
  },
};

const actions = {
  async fetchMergeQuestions({ commit }) {
    commit('setLoading', { loading: true });
    const papasMergeQuestions = await MergeInvestigatorApi.fetchPapasQuestions();
    const sonsMergeQuestions = await MergeInvestigatorApi.fetchSonsQuestions();
    commit('setPapasMergeQuestions', { papasMergeQuestions });
    commit('setSonsMergeQuestions', { sonsMergeQuestions });
    commit('setLoading', { loading: false });
  },
  async setPapaQuestionInput({ commit, state }, { transactionId, input }) {
    commit('setPapaQuestionInput', { transactionId, input });
  },
  async setPapaSource({ commit }, { transactionId, cardSource }) {
    commit('setPapaSource', { transactionId, cardSource });
  },
  async setSonQuestionInput({ commit, state }, { chunkId, input }) {
    commit('setSonsQuestionInput', { chunkId, input });
  },
  async addSonSource({ commit }, { chunkId, source: { sourceName } }) {
    commit('addSonSource', { chunkId, sourceName });
  },
  async finishMergeInvestigation({ state, commit }) {
    commit('setLoading', { loading: true });
    let createdNewCreds = false;
    const papaInputs = _.chain(state.papasMergeQuestions).reject(isAddCredsInput).map(extractPapasInput).value();
    const sonInputs = _.chain(state.sonsMergeQuestions).reject(isAddCredsInput).map(extractSonsInput).value();
    await MergeInvestigatorApi.answerMultipleQuestions(papaInputs, sonInputs);
    const papaSourcesToAdd = _.chain(state.papasMergeQuestions).filter(isAddCredsInput).map(q => q.cardSource)
      .value();
    const sonSourcesToAdd = _.chain(state.sonsMergeQuestions).filter(isAddCredsInput).map(q => q.bankSource)
      .value();
    const sourceNames = [...papaSourcesToAdd, ...sonSourcesToAdd];
    if (sourceNames.length > 0) {
      await CredentialsApi.createMultipleCredentials({ sourceNames });
      createdNewCreds = true;
    }
    commit('setLoading', { loading: false });
    return createdNewCreds;
  },
};

// mutations
const mutations = {
  setPapasMergeQuestions(state, { papasMergeQuestions }) {
    state.papasMergeQuestions = papasMergeQuestions;
  },
  setSonsMergeQuestions(state, { sonsMergeQuestions }) {
    state.sonsMergeQuestions = sonsMergeQuestions;
  },
  addSonSource(state, { chunkId, sourceName }) {
    const question = _.find(state.sonsMergeQuestions, q => q.chunkId === chunkId);
    Vue.set(question, 'bankSource', sourceName);
  },
  setPapaQuestionInput(state, { input, transactionId }) {
    const question = _.find(state.papasMergeQuestions, q => q.transactionId === transactionId);
    Vue.set(question, 'input', input);
  },
  setPapaSource(state, { cardSource, transactionId }) {
    const question = _.find(state.papasMergeQuestions, q => q.transactionId === transactionId);
    Vue.set(question, 'cardSource', cardSource);
  },
  setSonsQuestionInput(state, { input, chunkId }) {
    const question = _.find(state.sonsMergeQuestions, q => q.chunkId === chunkId);
    Vue.set(question, 'input', input);
  },
  setLoading(state, { loading }) {
    state.loading = loading;
  },
};

function isAddCredsInput(question) {
  return question.input === state.papaInputTypes.addCreds;
}

function extractPapasInput(papasQuestion) {
  return { transactionId: papasQuestion.transactionId, input: papasQuestion.input };
}

function extractSonsInput(sonsQuestion) {
  return { chunkId: sonsQuestion.chunkId, input: sonsQuestion.input, account: sonsQuestion.account };
}

function hasSource(question) {
  return !!(question.cardSource && _.map(sourcesFields.allSourceFields, 'sourceName').includes(question.cardSource));
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
